import { Fragment, useState, useEffect } from 'react';
import { styled,
    experimental_sx as sx
 } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Title from '../../shared/Title';
import FFPaper from '../../shared/FFPaper';
import DropDownButton, { DropDownMenu } from '../../shared/DropDownButton';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import DownloadButton from '../../shared/DownloadButton';
import { VisionAwareContext } from '../../../context/visionaware-context';
import { Dictionary } from '../../../model/base';
import { SeriousEventValue, TaggedSeriousOffender } from '../../../model/visionaware';
import BlueSkeleton from '../../shared/BlueSkeleton';
import { useTranslation } from 'react-i18next';

const TableRowText = styled(Typography)<TypographyProps>(({ theme }) => theme.unstable_sx({
    color: 'primary.main',
    fontWeight: 'bold',
    fontSize: {
        xs: 12,
        sm: 13,
        md: 16
    },
}));

const TableHeaderText = styled(Typography)<TypographyProps>(({ theme }) => theme.unstable_sx({
    color: 'text.primary',
    fontSize: {
        xs: 12,
        sm: 13,
        md: 16,
        textTransform: 'uppercase',
    },
}));

function TableHeader({ headers }: { headers: string[] }) {
    return (
        <Stack direction="row" sx={{ width: '100%', mb: 1 }}>
            <TableHeaderText sx={{ width: '30%' }}>{headers[0]}</TableHeaderText>
            {/* <TableHeaderText sx={{ width: '20%', textAlign: 'center' }}>{headers[1]}</TableHeaderText> */}
            <TableHeaderText sx={{ width: '25%', textAlign: 'center' }}>{headers[2]}</TableHeaderText>
            <TableHeaderText sx={{ width: '25%', textAlign: 'center' }}>{headers[3]}</TableHeaderText>
        </Stack>
    )
}

interface TableDataProps {
    data: TaggedSeriousOffender;
}

function AlertRow({ data }: TableDataProps) {

    return (
        <Stack direction="row" sx={{ width: '100%', height: 30, fontWeight: '500',
        fontSize: {
            xs: 14,
            md: 16,
            textTransform: 'uppercase',
        },
        color: '#686868' }}>
            <TableRowText sx={{ width: '30%' }}>{data.tagName}</TableRowText>
            {/* <TableRowText sx={{ width: '20%', textAlign: 'center' }}>{data.item2.value}</TableRowText> */}
            <TableRowText sx={{ width: '25%', textAlign: 'center' }}>{data.totalRespondedEvents}</TableRowText>
            <TableRowText sx={{ width: '25%', textAlign: 'center' }}>{data.totalDeterredEvents > 0 ? data.totalDeterredEvents : <RemoveOutlinedIcon fontSize="small" />}</TableRowText>
        </Stack>
    )
}

const exportMenus: DropDownMenu[] = [{ label: 'PDF', value: 'pdf' }, { label: 'CSV', value: 'csv' }];

export default function TaggedSeriousOffendersCard() {
    const { t } = useTranslation();

    const tableHeaders = [
        t('va_serious_heaader_1'),
        t('va_serious_heaader_2'),
        t('va_serious_heaader_3'),
        t('va_serious_heaader_4'),
    ]

    const [count, setCount] = useState(0);

    useEffect(() => {
        var intervalID = setInterval(() => { setCount(count => count + 1); }, 60000);
        return function cleanup() {
            clearInterval(intervalID);
        }
    }, []);

    return (
        <VisionAwareContext.Consumer>{
            ({ status, data, isFetching, error }) =>
                <Grid item={true} xs={12} >
                    <FFPaper
                        elevation={3}
                        sx={{
                            position: 'relative',
                            pl: 3,
                            pr: 3,
                            pb: 3,
                            height: '100%'
                        }}
                    >
                        <Stack direction="row"
                            sx={{
                                flexWrap: 'wrap',
                                fontWeight: '600',
                                fontSize: '16px',
                                textTransform: 'uppercase',
                                color: '#686868',
                            }}
                            spacing={1}>

                            <Title minWidth={300}
                                info={t('va_serious_tooltip')}
                                subtitle={`Last update ${count} min ago`}>
                                {t('va_serious_title')}
                            </Title>
                        </Stack>

                        <Stack sx={{ width: '100%', height: '100%', justifyContent: 'start', mt: 2 }} >

                            {isFetching &&
                                <Fragment>
                                    <Typography variant="h4">
                                        <BlueSkeleton width="100%" sx={{ ml: 'auto', mr: 'auto' }} />
                                    </Typography>
                                    <Typography variant="body1">
                                        <BlueSkeleton variant="rectangular" width="100%"
                                            sx={{ ml: 'auto', mr: 'auto' }}
                                            height={280} />
                                    </Typography>
                                </Fragment>
                            }

                            {status === 'success' && !isFetching && <TableHeader headers={tableHeaders} />}

                            {status === 'success' && !isFetching && data && data.taggedSeriousOffenders && Object.entries(data.taggedSeriousOffenders).map(
                                ([k, v]) => k !== 'tagSummary' && <AlertRow key={k} data={v} />
                            )}

                        </Stack>
                    </FFPaper>
                </Grid>
        }

        </VisionAwareContext.Consumer>
    )

}
